import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ClinicInfo } from '@/types/clinic';
import { BookingApi } from '@/utils/api';
import { useToastContext } from '@/contexts/ToastContext';
import { ProgressBar } from '@/components/ProgressBar';
import { LoadingSpinner } from '@/components/LoadingSpinner';
import { Footer } from '@/components/Footer';

interface PatientInfo {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  email: string;
  dateOfBirth: {
    month: string;
    day: string;
    year: string;
  };
}

export function PatientInfoPage() {
  const { clinicSlug } = useParams<{ clinicSlug: string }>();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToastContext();
  const [clinic, setClinic] = useState<ClinicInfo | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [patientInfo, setPatientInfo] = useState<PatientInfo>({
    firstName: '',
    lastName: '',
    phoneNumber: '',
    email: '',
    dateOfBirth: {
      month: '',
      day: '',
      year: '',
    },
  });

  const [errors, setErrors] = useState<Partial<PatientInfo>>({});
  const [isVerifying, setIsVerifying] = useState(false);

  // Cookie helper function
  const setCookie = (name: string, value: string) => {
    document.cookie = `${name}=${encodeURIComponent(value)}; path=/; max-age=604800`;
  };

  useEffect(() => {
    const fetchClinic = async () => {
      if (!clinicSlug) {
        setError('No clinic slug provided');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);

      try {
        const config = await BookingApi.getClinicConfigBySlug(clinicSlug);
        const cs = (config.active_theme?.theme?.color_scheme ||
          config.active_theme?.color_scheme) as unknown as Record<
          string,
          string
        >;
        const clinicData: ClinicInfo = {
          id: String(config.location.id),
          name: config.location.name,
          slug: config.location.slug,
          description: config.ai_agent?.greeting_message || '',
          address: '',
          phone: '',
          email: '',
          website: undefined,
          theme: {
            primaryColor: cs?.primary || '#2563eb',
            secondaryColor: cs?.secondary || '#1d4ed8',
            accentColor: cs?.main || cs?.primary || '#2563eb',
            backgroundColor: '#FFFFFF',
            textColor: cs?.['text-color'] || '#1F2937',
            mainColor: cs?.primary || '#2563eb',
            gradient: (cs?.gradient as string) ?? null,
            iconStroke: cs?.['icon-stroke'] || cs?.secondary || undefined,
          },
          services: [],
          doctors: [],
          workingHours: {
            monday: { isOpen: false },
            tuesday: { isOpen: false },
            wednesday: { isOpen: false },
            thursday: { isOpen: false },
            friday: { isOpen: false },
            saturday: { isOpen: false },
            sunday: { isOpen: false },
          },
          isActive: true,
          aiAgent: config.ai_agent,
        };

        if (clinicData) {
          setClinic(clinicData);
        } else {
          setError('Clinic not found');
        }
      } catch (err) {
        setError('Error loading clinic information');
        console.error('Error fetching clinic:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchClinic();
  }, [clinicSlug]);

  // Theme is initialized globally in App via ThemeInitializer

  const validateForm = (): boolean => {
    const newErrors: Partial<PatientInfo> = {};

    if (!patientInfo.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!patientInfo.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!patientInfo.phoneNumber.trim()) {
      newErrors.phoneNumber = 'Phone number is required';
    }

    if (!patientInfo.email.trim()) {
      newErrors.email = 'Email is required';
    }

    if (
      !patientInfo.dateOfBirth.month ||
      !patientInfo.dateOfBirth.day ||
      !patientInfo.dateOfBirth.year
    ) {
      newErrors.dateOfBirth = {
        month: 'Date of birth is required',
        day: '',
        year: '',
      };
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof PatientInfo, value: string) => {
    setPatientInfo(prev => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  const handleDateChange = (field: 'month' | 'day' | 'year', value: string) => {
    setPatientInfo(prev => ({
      ...prev,
      dateOfBirth: {
        ...prev.dateOfBirth,
        [field]: value,
      },
    }));

    // Clear date error when user starts typing
    if (errors.dateOfBirth) {
      setErrors(prev => ({
        ...prev,
        dateOfBirth: undefined,
      }));
    }
  };

  const handleNext = async () => {
    if (validateForm()) {
      if (!clinicSlug) return;

      setIsVerifying(true);

      try {
        const patientData = {
          patient_name: `${patientInfo.firstName} ${patientInfo.lastName}`,
          patient_dob: `${patientInfo.dateOfBirth.year}-${patientInfo.dateOfBirth.month}-${patientInfo.dateOfBirth.day}`,
          patient_phone: patientInfo.phoneNumber,
          patient_email: patientInfo.email,
          first_name: patientInfo.firstName,
          last_name: patientInfo.lastName,
        };

        // Call verify-returning API
        const result = await BookingApi.verifyReturningPatient(
          clinicSlug,
          patientData
        );

        if (result && (result as any).data) {
          const verified = (result as any).data?.data?.patient_verified;
          if (verified) {
            // Patient is verified - show success message and navigate
            console.log('Patient verified:', result.data.message);
            showSuccess(
              result.data.message || 'Welcome back! We found your information.'
            );
          } else {
            // Patient not found - continue with normal flow
            console.log('Patient not found, continuing with new patient flow');
          }
        } else {
          // API error - show error and continue with normal flow
          console.error('Error verifying patient:', result.error);
          showError(
            'Unable to verify patient information. Continuing with new patient flow.'
          );
        }

        // Save patient information to cookies for use in final step
        setCookie('patient_first_name', patientInfo.firstName);
        setCookie('patient_last_name', patientInfo.lastName);
        setCookie('patient_phone_number', patientInfo.phoneNumber);
        setCookie('patient_email', patientInfo.email);
        setCookie(
          'patient_dob',
          `${patientInfo.dateOfBirth.year}-${patientInfo.dateOfBirth.month}-${patientInfo.dateOfBirth.day}`
        );
        setCookie(
          'patient_full_name',
          `${patientInfo.firstName} ${patientInfo.lastName}`
        );

        navigate(`/${clinicSlug}/insurance`);
      } catch (error) {
        console.error('Error during patient verification:', error);
        showError(
          'An error occurred while verifying patient information. Continuing with new patient flow.'
        );

        // Save patient information to cookies even on error
        setCookie('patient_first_name', patientInfo.firstName);
        setCookie('patient_last_name', patientInfo.lastName);
        setCookie('patient_phone_number', patientInfo.phoneNumber);
        setCookie('patient_email', patientInfo.email);
        setCookie(
          'patient_dob',
          `${patientInfo.dateOfBirth.year}-${patientInfo.dateOfBirth.month}-${patientInfo.dateOfBirth.day}`
        );
        setCookie(
          'patient_full_name',
          `${patientInfo.firstName} ${patientInfo.lastName}`
        );

        // Continue with normal flow on error
        navigate(`/${clinicSlug}/insurance`);
      } finally {
        setIsVerifying(false);
      }
    }
  };

  const isFormValid = () => {
    return (
      patientInfo.firstName.trim() &&
      patientInfo.lastName.trim() &&
      patientInfo.phoneNumber.trim() &&
      patientInfo.email.trim() &&
      patientInfo.dateOfBirth.month &&
      patientInfo.dateOfBirth.day &&
      patientInfo.dateOfBirth.year
    );
  };

  if (loading) {
    return (
      <div className='min-h-screen relative'>
        <LoadingSpinner fullScreen size='lg' text='Loading...' />
      </div>
    );
  }

  if (error || !clinic) {
    return (
      <div className='min-h-screen relative flex items-center justify-center'>
        <div className='text-center'>
          <h1 className='text-4xl font-bold text-gray-800 mb-4'>404</h1>
          <p className='text-xl text-gray-600 mb-8'>Clinic not found</p>
        </div>
      </div>
    );
  }

  return (
    <div className='h-screen w-screen overflow-hidden relative flex flex-col'>
      {/* Header with Logo */}
      <div className='flex-shrink-0 p-6'>
        <div className='flex items-center'>
          <div className='w-fit'>
            <img
              src={'/logo.png'}
              alt={`${clinic.name} logo`}
              className='w-full h-full object-contain'
            />
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className='flex-1 flex flex-col justify-center px-6 py-8'>
        <div className='max-w-3xl mx-auto w-full'>
          {/* Progress Bar and Navigation */}
          <div className='flex flex-row justify-center items-center gap-4'>
            <div className='flex items-center justify-between'>
              <button
                onClick={() => navigate(`/${clinicSlug}/patient-visit`)}
                className='text-gray-600 hover:text-gray-800 flex items-center space-x-2'
              >
                <svg
                  className='w-4 h-4'
                  fill='none'
                  stroke='var(--clinic-icon-stroke)'
                  viewBox='0 0 24 24'
                >
                  <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M15 19l-7-7 7-7'
                  />
                </svg>
                <span>Prev</span>
              </button>
            </div>

            {/* Progress Bar */}
            <ProgressBar progress={80} animated={false} />
          </div>

          {/* Title */}
          <div className='text-center mb-12 mt-8 px-8'>
            <h2 className='text-xl lg:text-3xl font-semibold text-gray-900'>
              Please provide the patient's details below.
            </h2>
          </div>

          {/* Form Card */}
          <div className='bg-white rounded-2xl shadow-lg p-8 mb-8'>
            <div className='space-y-6'>
              {/* First Name */}
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>
                  First Name
                </label>
                <input
                  type='text'
                  value={patientInfo.firstName}
                  onChange={e => handleInputChange('firstName', e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent transition-colors ${
                    errors.firstName ? 'border-red-500' : 'border-gray-300'
                  }`}
                  style={
                    {
                      '--tw-ring-color':
                        clinic?.theme.primaryColor || '#20B2AA',
                    } as React.CSSProperties
                  }
                  placeholder='Enter first name'
                />
                {errors.firstName && (
                  <p className='text-red-500 text-sm mt-1'>
                    {errors.firstName}
                  </p>
                )}
              </div>

              {/* Last Name */}
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>
                  Last Name
                </label>
                <input
                  type='text'
                  value={patientInfo.lastName}
                  onChange={e => handleInputChange('lastName', e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent transition-colors ${
                    errors.lastName ? 'border-red-500' : 'border-gray-300'
                  }`}
                  style={
                    {
                      '--tw-ring-color':
                        clinic?.theme.primaryColor || '#20B2AA',
                    } as React.CSSProperties
                  }
                  placeholder='Enter last name'
                />
                {errors.lastName && (
                  <p className='text-red-500 text-sm mt-1'>{errors.lastName}</p>
                )}
              </div>
              {/* Phone Number */}
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>
                  Phone Number
                </label>
                <input
                  type='text'
                  value={patientInfo.phoneNumber}
                  onChange={e =>
                    handleInputChange('phoneNumber', e.target.value)
                  }
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent transition-colors ${
                    errors.phoneNumber ? 'border-red-500' : 'border-gray-300'
                  }`}
                  style={
                    {
                      '--tw-ring-color':
                        clinic?.theme.primaryColor || '#20B2AA',
                    } as React.CSSProperties
                  }
                  placeholder='Enter phone number'
                />
                {errors.phoneNumber && (
                  <p className='text-red-500 text-sm mt-1'>
                    {errors.phoneNumber}
                  </p>
                )}
              </div>
              {/* Email */}
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>
                  Email
                </label>
                <input
                  type='text'
                  value={patientInfo.email}
                  onChange={e => handleInputChange('email', e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent transition-colors ${
                    errors.email ? 'border-red-500' : 'border-gray-300'
                  }`}
                  style={
                    {
                      '--tw-ring-color':
                        clinic?.theme.primaryColor || '#20B2AA',
                    } as React.CSSProperties
                  }
                  placeholder='Enter email'
                />
                {errors.email && (
                  <p className='text-red-500 text-sm mt-1'>{errors.email}</p>
                )}
              </div>
              {/* Date of Birth */}
              <div>
                <label className='block text-sm font-medium text-gray-700 mb-2'>
                  Date of Birth
                </label>
                <div className='grid grid-cols-3 gap-4'>
                  <div>
                    <label className='block text-xs text-gray-500 mb-1'>
                      Month
                    </label>
                    <select
                      value={patientInfo.dateOfBirth.month}
                      onChange={e => handleDateChange('month', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent transition-colors appearance-none bg-white text-sm ${
                        errors.dateOfBirth
                          ? 'border-red-500'
                          : 'border-gray-300'
                      }`}
                      style={
                        {
                          '--tw-ring-color':
                            clinic?.theme.primaryColor || '#20B2AA',
                        } as React.CSSProperties
                      }
                    >
                      <option value=''>MM</option>
                      {Array.from({ length: 12 }, (_, i) => (
                        <option
                          key={i + 1}
                          value={String(i + 1).padStart(2, '0')}
                        >
                          {String(i + 1).padStart(2, '0')}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className='block text-xs text-gray-500 mb-1'>
                      Day
                    </label>
                    <select
                      value={patientInfo.dateOfBirth.day}
                      onChange={e => handleDateChange('day', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent transition-colors appearance-none bg-white text-sm ${
                        errors.dateOfBirth
                          ? 'border-red-500'
                          : 'border-gray-300'
                      }`}
                      style={
                        {
                          '--tw-ring-color':
                            clinic?.theme.primaryColor || '#20B2AA',
                        } as React.CSSProperties
                      }
                    >
                      <option value=''>DD</option>
                      {Array.from({ length: 31 }, (_, i) => (
                        <option
                          key={i + 1}
                          value={String(i + 1).padStart(2, '0')}
                        >
                          {String(i + 1).padStart(2, '0')}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className='block text-xs text-gray-500 mb-1'>
                      Year
                    </label>
                    <select
                      value={patientInfo.dateOfBirth.year}
                      onChange={e => handleDateChange('year', e.target.value)}
                      className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:border-transparent transition-colors appearance-none bg-white text-sm ${
                        errors.dateOfBirth
                          ? 'border-red-500'
                          : 'border-gray-300'
                      }`}
                      style={
                        {
                          '--tw-ring-color':
                            clinic?.theme.primaryColor || '#20B2AA',
                        } as React.CSSProperties
                      }
                    >
                      <option value=''>YYYY</option>
                      {Array.from({ length: 100 }, (_, i) => {
                        const year = new Date().getFullYear() - i;
                        return (
                          <option key={year} value={year}>
                            {year}
                          </option>
                        );
                      })}
                    </select>
                  </div>
                </div>
                {errors.dateOfBirth && (
                  <p className='text-red-500 text-sm mt-1'>
                    Date of birth is required
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* Next Button */}
          <div className='flex justify-center'>
            <button
              disabled={!isFormValid() || isVerifying}
              onClick={handleNext}
              className={`h-[54px] min-w-[300px] px-6 py-3 rounded-xl text-base transition-all duration-300 flex items-center justify-center gap-2.5 ${
                isFormValid() && !isVerifying
                  ? 'text-white shadow-[var(--shadow-xs)] font-semibold hover:opacity-90'
                  : 'bg-[var(--color-gray-100)] text-[var(--color-gray-500)] shadow-[var(--shadow-xs)] font-normal cursor-not-allowed'
              }`}
              style={
                isFormValid() && !isVerifying
                  ? { backgroundColor: 'var(--clinic-primary)' }
                  : {}
              }
            >
              {isVerifying ? (
                <>
                  <LoadingSpinner size='sm' text='' />
                  Verifying...
                </>
              ) : (
                'Next'
              )}
            </button>
          </div>
        </div>
      </div>

      {/* Footer */}
      <Footer mobileColumnLayout={true} />
    </div>
  );
}
